import { ActionIcon, CloseButton, TextInput, TextInputProps, useMantineTheme } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { forwardRef, useState } from 'react';
import { usePreferencesStore } from '../../../stores';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export interface SearchInputProps extends Omit<TextInputProps, 'onChange'> {
  onSearch?: (value: string) => void;
  onChange?: (value: string) => void;
  onClear?: () => void;
  gradientVariant?: GradientVariant;
  showClearButton?: boolean;
  useColorScheme?: boolean; // Use color scheme from state management
}

const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      onSearch,
      onChange,
      onClear,
      gradientVariant = 'primary',
      showClearButton = true,
      useColorScheme = true,
      value: controlledValue,
      defaultValue,
      ...rest
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const colorSchemeKey = usePreferencesStore((state) => state.colorSchemeKey);
    const reducedMotion = usePreferencesStore((state) => state.reducedMotion);
    const strongFocus = usePreferencesStore((state) => state.strongFocus);

    // Determine which color to use
    let color = theme.other.gradients[gradientVariant].from;

    if (useColorScheme && colorSchemeKey) {
      const colorScheme = theme.other.colorSchemes?.find((cs: any) => cs.key === colorSchemeKey);
      if (colorScheme) {
        color = colorScheme.primary.from;
      }
    }

    const [internalValue, setInternalValue] = useState(defaultValue?.toString() || '');

    const value = controlledValue !== undefined ? controlledValue : internalValue;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      if (controlledValue === undefined) {
        setInternalValue(newValue);
      }
      onChange?.(newValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && onSearch) {
        onSearch(value.toString());
      }
    };

    const handleClear = () => {
      if (controlledValue === undefined) {
        setInternalValue('');
      }
      onChange?.('');
      onClear?.();
    };

    return (
      <TextInput
        ref={ref}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder="Search..."
        aria-label="Search input"
        leftSection={
          <IconSearch
            size={16}
            style={{
              color: color,
            }}
            aria-hidden="true"
          />
        }
        rightSection={
          showClearButton && value ? (
            <CloseButton
              size="sm"
              onClick={handleClear}
              aria-label="Clear search"
              style={{ color: color }}
            />
          ) : null
        }
        styles={{
          label: {
            textAlign: 'left !important' as any,
          },
          input: {
            transition: reducedMotion ? 'none' : 'border-color 0.2s ease, box-shadow 0.2s ease',
            paddingLeft: '36px', // Add space for icon
            '&:focus': {
              borderColor: color,
              boxShadow: strongFocus
                ? `0 0 0 3px ${color}50`
                : `0 0 0 2px ${color}30`,
              outline: strongFocus ? `2px solid ${color}` : 'none',
              outlineOffset: strongFocus ? '2px' : '0',
            },
            '&:focus-visible': {
              outline: `2px solid ${color}`,
              outlineOffset: '2px',
            },
          },
          section: {
            gap: '8px', // Add gap between icon and text
          },
        }}
        {...rest}
      />
    );
  }
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
